import { attributes } from '../../core/ids-attributes';

/**
 * Prefix for CSS class names
 */
export const prefix = 'ids-form-layout';

/**
 * Tracks, per child element, the set of attribute names a given propagator
 * (form-layout / group / inline) has pushed onto it. This lets us tell apart
 * values the user authored directly on a child (which we must never touch)
 * from values we inherited down — so that when a parent setting is removed or
 * changed, we can correctly clear/update the inherited value on the child.
 */
export type PropagatedKeys = WeakMap<HTMLElement, Set<string>>;

/**
 * Tag names of form-layout sub-containers that manage their own descendants.
 * Propagation stops at these boundaries so each container owns its children.
 */
export const NESTED_LAYOUT_TAGS = ['ids-form-layout-group', 'ids-form-layout-inline'];

/**
 * Tag names of supported child form controls.
 */
export const FORM_CONTROL_TAGS = [
  'ids-input',
  'ids-textarea',
  'ids-dropdown',
  'ids-date-picker',
  'ids-time-picker',
  'ids-checkbox',
  'ids-radio',
  'ids-lookup',
  'ids-color-picker',
  'ids-search-field',
  'ids-spinbox',
  'ids-multiselect',
  'ids-upload',
  'ids-editor'
];

/**
 * Collect the direct propagation targets for a form-layout container.
 *
 * Walks descendants but does NOT descend into nested form-layout containers
 * (group / inline) — those are returned as targets themselves and are
 * responsible for propagating to their own children. This prevents an outer
 * container from reaching into (and clobbering) the controls a nested wrapper
 * manages, e.g. applying `validate` to inputs inside an inline group.
 * @param {HTMLElement} root the container whose children to collect
 * @returns {HTMLElement[]} the elements to propagate settings to
 */
export function collectPropagationTargets(root: HTMLElement): HTMLElement[] {
  const targets: HTMLElement[] = [];
  const walk = (node: Element) => {
    Array.from(node.children).forEach((el) => {
      const tag = el.tagName.toLowerCase();
      if (NESTED_LAYOUT_TAGS.includes(tag) || FORM_CONTROL_TAGS.includes(tag)) {
        targets.push(el as HTMLElement);
        // Stop here: the nested container/control owns anything below it.
        return;
      }
      // Plain wrapper element — keep descending to find controls inside it.
      walk(el);
    });
  };
  walk(root);
  return targets;
}

/**
 * Synchronize a single inherited setting from a parent onto a child.
 *
 * Behavior:
 * - If the child has the attribute and we did NOT propagate it, the value is
 *   user-authored — leave it untouched (child always wins).
 * - If the parent has a value, set it on the child (via the property setter)
 *   and remember that we own this attribute on that child.
 * - If the parent has no value but we previously propagated one, clear it
 *   (set the property to `null`/`false`) and forget our ownership.
 * @param {HTMLElement} child the child element to update
 * @param {string} attr the observed attribute name that backs the property
 * @param {string} prop the property name to assign on the child
 * @param {unknown} parentValue the resolved value from the parent (falsy = unset)
 * @param {PropagatedKeys} tracker per-propagator map of propagated attribute keys
 * @param {boolean} isBoolean when true, clearing assigns `false` instead of `null`
 */
export function syncSettingToChild(
  child: HTMLElement,
  attr: string,
  prop: string,
  parentValue: unknown,
  tracker: PropagatedKeys,
  isBoolean = false
): void {
  if (!(prop in child)) return;

  let owned = tracker.get(child);
  const weOwnIt = owned?.has(attr) ?? false;

  // User authored this attribute on the child directly — never override it.
  if (child.hasAttribute(attr) && !weOwnIt) return;

  if (parentValue) {
    (child as any)[prop] = isBoolean ? true : parentValue;
    if (!owned) {
      owned = new Set<string>();
      tracker.set(child, owned);
    }
    owned.add(attr);
  } else if (weOwnIt) {
    // Parent value was removed — clear the inherited value we set earlier.
    (child as any)[prop] = isBoolean ? false : null;
    owned!.delete(attr);
  }
}

/**
 * Observed attributes for IdsFormLayout
 */
export const FORM_LAYOUT_ATTRIBUTES: string[] = [
  attributes.COLS,
  attributes.COLS_XS,
  attributes.COLS_SM,
  attributes.COLS_MD,
  attributes.COLS_LG,
  attributes.COLS_XL,
  attributes.COLS_XXL,
  attributes.GAP,
  attributes.ROW_GAP,
  attributes.COL_GAP,
  // Label position attributes
  attributes.LABEL_POSITION,
  attributes.LABEL_WIDTH,
  attributes.LABEL_WRAP,
  attributes.LABEL_BREAK,
  attributes.LABEL_ALIGN_Y,
  attributes.LABEL_ALIGNMENT,
  // Label state attribute (propagates to children)
  attributes.LABEL_STATE,
  // Field height attributes (Milestone 3)
  attributes.COMPACT,
  attributes.FIELD_HEIGHT,
  // Field spacing attributes (Milestone 4)
  attributes.FIELD_SPACING,
  attributes.NO_MARGINS,
  // Container query attribute (Milestone 7)
  attributes.ENABLE_CONTAINER,
  // Align items attribute
  attributes.ALIGN_ITEMS,
  // Size attribute (propagates to children)
  attributes.SIZE,
  // Validation attribute (propagates to children)
  attributes.VALIDATE
];

/**
 * Label position type
 */
export type IdsFormLayoutLabelPosition = 'top' | 'inline-start' | 'inline-end' | null;

/**
 * Valid label positions
 */
export const LABEL_POSITIONS: Array<IdsFormLayoutLabelPosition> = [
  null,
  'top',
  'inline-start',
  'inline-end'
];

/**
 * Label wrap type
 */
export type IdsFormLayoutLabelWrap = 'ellipsis' | 'wrap' | 'ellipsis-no-stretch' | 'wrap-no-stretch' | null;

/**
 * Valid label wraps
 */
export const LABEL_WRAPS: Array<IdsFormLayoutLabelWrap> = [
  null,
  'ellipsis',
  'wrap',
  'ellipsis-no-stretch',
  'wrap-no-stretch'
];

/**
 * Label break type (controls how label and value behave when space is constrained)
 */
export type IdsFormLayoutLabelBreak = 'shrink-value' | 'shrink-label' | 'break' | null;

/**
 * Valid label break values
 */
export const LABEL_BREAKS: Array<IdsFormLayoutLabelBreak> = [
  null,
  'shrink-value',
  'shrink-label',
  'break'
];

/**
 * Label vertical alignment type
 */
export type IdsFormLayoutLabelAlignY = 'baseline' | 'center' | 'flex-start' | 'flex-end' | 'stretch' | null;

/**
 * Valid label vertical alignments
 */
export const LABEL_ALIGN_Y_VALUES: Array<IdsFormLayoutLabelAlignY> = [
  null,
  'baseline',
  'center',
  'flex-start',
  'flex-end',
  'stretch'
];

/**
 * Label text alignment type
 */
export type IdsFormLayoutLabelAlignment = 'start' | 'end' | null;

/**
 * Valid label text alignments
 */
export const LABEL_ALIGNMENTS: Array<IdsFormLayoutLabelAlignment> = [
  null,
  'start',
  'end'
];

/**
 * Gap type values
 */
export type IdsFormLayoutGapType = undefined | 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Valid gap types array
 */
export const GAP_TYPES: Array<IdsFormLayoutGapType> = [
  undefined,
  'none',
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl'
];

/**
 * Maximum number of columns supported
 */
export const MAX_COLS = 12;

/**
 * Field height type
 */
export type IdsFormLayoutFieldHeight = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | null;

/**
 * Valid field heights
 */
export const FIELD_HEIGHTS: Array<IdsFormLayoutFieldHeight> = [
  null,
  'xxs',
  'xs',
  'sm',
  'md',
  'lg'
];

/**
 * Margin block end / field spacing type (matches IdsInput MarginBlockEndKey)
 */
export type IdsFormLayoutFieldSpacing = 'none' | '4xs' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | null;

/**
 * Valid field spacing values
 */
export const FIELD_SPACING_VALUES: Array<IdsFormLayoutFieldSpacing> = [
  null,
  'none',
  '4xs',
  '3xs',
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl'
];

/**
 * Align items type for grid alignment
 */
export type IdsFormLayoutAlignItems = 'anchor-center' | 'center' | 'baseline' | 'end' | 'start' | null;

/**
 * Valid align items values
 */
export const ALIGN_ITEMS_VALUES: Array<IdsFormLayoutAlignItems> = [
  null,
  'anchor-center',
  'center',
  'baseline',
  'end',
  'start'
];

/**
 * Size type for form controls (matches IdsInput sizes)
 */
export type IdsFormLayoutSize = 'xs' | 'sm' | 'mm' | 'md' | 'lg' | 'full' | null;

/**
 * Valid size values
 */
export const SIZE_VALUES: Array<IdsFormLayoutSize> = [
  null,
  'xs',
  'sm',
  'mm',
  'md',
  'lg',
  'full'
];

/**
 * Observed attributes for IdsFormLayoutGroup
 */
export const FORM_LAYOUT_GROUP_ATTRIBUTES: string[] = [
  attributes.COLS,
  attributes.COLS_XS,
  attributes.COLS_SM,
  attributes.COLS_MD,
  attributes.COLS_LG,
  attributes.COLS_XL,
  attributes.COLS_XXL,
  attributes.COL_SPAN,
  attributes.COL_SPAN_XS,
  attributes.COL_SPAN_SM,
  attributes.COL_SPAN_MD,
  attributes.COL_SPAN_LG,
  attributes.COL_SPAN_XL,
  attributes.COL_SPAN_XXL,
  attributes.LABEL,
  attributes.GAP,
  attributes.LABEL_STATE,
  attributes.ENABLE_CONTAINER
];

/**
 * Observed attributes for IdsFormLayoutInline
 */
export const FORM_LAYOUT_INLINE_ATTRIBUTES: string[] = [
  attributes.LABEL,
  attributes.LABEL_POSITION,
  attributes.LABEL_WIDTH,
  attributes.LABEL_ALIGNMENT,
  attributes.LABEL_WRAP,
  attributes.LABEL_STATE,
  attributes.GAP,
  attributes.COLUMNS,
  attributes.COL_SPAN,
  attributes.COL_SPAN_XS,
  attributes.COL_SPAN_SM,
  attributes.COL_SPAN_MD,
  attributes.COL_SPAN_LG,
  attributes.COL_SPAN_XL,
  attributes.COL_SPAN_XXL,
  attributes.WRAP,
  attributes.REQUIRED,
  attributes.DISABLED,
  attributes.READONLY,
  attributes.COMPACT,
  attributes.FIELD_HEIGHT,
  attributes.SIZE,
  attributes.ENABLE_CONTAINER
];
